import type { BillingDetails, Invoice, InvoiceDocument, InvoiceLine } from "@otrack/domain";
import { docDate, docMoney, docPercent, docPeriod, docQuantity, invoiceCopy, lineTitle, type InvoiceCopy } from "./invoice-copy";

function Party({ details, copy, missing }: { details: BillingDetails; copy: InvoiceCopy; missing?: string }) {
  return <>
    <strong className="ot-paper__party-name">{details.name || <span className="ot-paper__missing">{missing}</span>}</strong>
    {details.address && <address className="ot-paper__address">{details.address}</address>}
    {details.email && <span>{details.email}</span>}
    {details.taxId && <span>{copy.taxId} <span className="ot-mono">{details.taxId}</span></span>}
  </>;
}

/** The line's second row: where the time went, the day of a single entry, or that it is a balance. */
function lineContext(line: InvoiceLine, copy: InvoiceCopy): string | null {
  const parts = [line.context, line.date !== null ? docDate(line.date, copy) : null, line.kind === "balance" ? copy.balance : null].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

/**
 * The Invoice as the client receives it: always on light paper, in the Client's language, with
 * Ember used once, as the strata mark beside the amount due. The same markup prints to PDF.
 */
export function InvoicePaper({ invoice, document }: { invoice: Invoice; document: InvoiceDocument }) {
  const copy = invoiceCopy(document.language);
  const money = (amount: number) => docMoney(amount, invoice.currency, copy);
  const { totals, whole, installment, shared } = document;
  const draft = invoice.status === "draft";

  return <article className={`ot-paper ${draft ? "is-draft" : ""}`} lang={document.language} aria-label={`${copy.invoice} ${invoice.number ?? copy.draft}`}>
    <header className="ot-paper__head">
      <div className="ot-paper__party ot-paper__issuer">
        {document.issuer.logo && <img className="ot-paper__logo" src={document.issuer.logo} alt={document.issuer.name}/>}
        <Party details={document.issuer} copy={copy} missing="Your business name"/>
      </div>
      <div className="ot-paper__title">
        <h2>{copy.invoice}</h2>
        <span className="ot-paper__number ot-mono">{invoice.number ?? copy.draft}</span>
        {installment && <span className="ot-paper__reference">{copy.installment(installment.index, installment.count)}</span>}
        {shared.reference && <span className="ot-paper__reference">{copy.reference} <span className="ot-mono">{shared.reference}</span></span>}
      </div>
    </header>

    <section className="ot-paper__meta">
      <div className="ot-paper__party">
        <span className="ot-paper__label">{copy.billedTo}</span>
        <Party details={document.recipient} copy={copy}/>
      </div>
      <dl className="ot-paper__dates">
        <div><dt className="ot-paper__label">{copy.issued}</dt><dd>{invoice.issueDate !== null && !draft ? docDate(invoice.issueDate, copy) : <span className="ot-paper__pending">{copy.onSend}</span>}</dd></div>
        <div><dt className="ot-paper__label">{copy.due}</dt><dd>{invoice.dueDate !== null && !draft ? docDate(invoice.dueDate, copy) : <span className="ot-paper__pending">{copy.dueAfter(invoice.paymentTermDays)}</span>}</dd></div>
        <div><dt className="ot-paper__label">{copy.period}</dt><dd>{docPeriod(shared, copy)}</dd></div>
      </dl>
    </section>

    <table className="ot-paper__lines">
      <thead>
        <tr>
          <th scope="col">{copy.description}</th>
          <th scope="col" className="is-num">{copy.quantity}</th>
          <th scope="col" className="is-num">{copy.rate}</th>
          <th scope="col" className="is-num">{copy.amount}</th>
        </tr>
      </thead>
      <tbody>
        {document.lines.map((line) => {
          const context = lineContext(line, copy);
          return <tr key={line.key}>
            <td>
              <span className="ot-paper__line-title">{lineTitle(line, copy)}</span>
              {context && <span className="ot-paper__line-context">{context}</span>}
              {line.notes.length > 0 && <span className="ot-paper__line-notes">{line.notes.join("; ")}</span>}
            </td>
            <td className="is-num ot-mono">{docQuantity(line, copy)}</td>
            <td className="is-num ot-mono">{money(line.unitPrice)}</td>
            <td className="is-num ot-mono">{money(line.amount)}</td>
          </tr>;
        })}
        {document.lines.length === 0 && <tr className="ot-paper__empty"><td colSpan={4}>Nothing to bill yet. Include time, a fixed price item, or a line from the panel.</td></tr>}
      </tbody>
    </table>

    <section className="ot-paper__totals">
      <dl>
        <div><dt>{copy.subtotal}</dt><dd className="ot-mono">{money(whole.subtotal)}</dd></div>
        {/* An Installment bills its share of the lines; discount and tax follow that share. */}
        {installment && <div>
          <dt>{copy.installment(installment.index, installment.count)}, {docPercent(installment.percent, copy)}</dt>
          <dd className="ot-mono">{money(totals.subtotal)}</dd>
        </div>}
        {totals.discount > 0 && <div>
          <dt>{copy.discount}{shared.discount?.type === "percent" ? ` ${docPercent(shared.discount.value, copy)}` : ""}</dt>
          <dd className="ot-mono">{"−"}{money(totals.discount)}</dd>
        </div>}
        {totals.taxes.map((tax) => <div key={tax.id}><dt>{tax.name || "Tax"} {docPercent(tax.rate, copy)}</dt><dd className="ot-mono">{money(tax.amount)}</dd></div>)}
        <div className="ot-paper__due">
          <dt><i className="ot-paper__mark" aria-hidden="true"/>{copy.amountDue}</dt>
          <dd className="ot-mono">{money(totals.total)}</dd>
        </div>
      </dl>
      {invoice.status === "paid" && invoice.paidAt !== null && <p className="ot-paper__paid">{copy.paidOn(docDate(invoice.paidAt, copy))}</p>}
    </section>

    {(document.paymentInstructions || invoice.note) && <footer className="ot-paper__foot">
      {document.paymentInstructions && <div>
        <span className="ot-paper__label">{copy.payment}</span>
        <p>{document.paymentInstructions}</p>
      </div>}
      {invoice.note && <div>
        <span className="ot-paper__label">{copy.note}</span>
        <p>{invoice.note}</p>
      </div>}
    </footer>}
  </article>;
}
